/** @format */

import { TOrgStructure } from "../../../../redux-modules/declarations/maps/organigramma";

interface TNamed {
  name?: string | null;
  surname?: string | null;
}

/** "GIANPAOLO GIORDANO" -> "Gianpaolo Giordano" */
export const titleCase = (value: string = ""): string =>
  value
    .toLowerCase()
    .replace(/(^|[\s'-])([a-zà-öø-ÿ])/g, (_match, sep: string, ch: string) => sep + ch.toUpperCase());

export const fullNameOf = (person?: TNamed | null): string =>
  titleCase(`${person?.name ?? ""} ${person?.surname ?? ""}`.trim());

/** Per gli elenchi: "Cognome Nome" */
export const surnameFirstOf = (person?: TNamed | null): string =>
  titleCase(`${person?.surname ?? ""} ${person?.name ?? ""}`.trim());

export const initialsOf = (person?: TNamed | null): string =>
  `${(person?.name ?? "").trim().charAt(0)}${(person?.surname ?? "").trim().charAt(0)}`.toUpperCase() ||
  "?";

/** Testo su cui cercare: nome in entrambi gli ordini + ruolo. */
export const searchHaystack = (person: TNamed & { role_name?: string | null }): string =>
  [
    `${person.name ?? ""} ${person.surname ?? ""}`,
    `${person.surname ?? ""} ${person.name ?? ""}`,
    person.role_name ?? "",
  ]
    .join("|")
    .toLowerCase();

/**
 * Separatore tra ruolo e pillole nella card: uno spazio "en" (U+2002) che, a
 * differenza di un margine, resta a fine riga quando la pillola va a capo.
 */
export const META_SEPARATOR = "\u2002";

/** La pillola "Staff" serve solo se il ruolo non si chiama già "Staff". */
export const showsStaffPill = (person: { is_staff?: boolean; role_name?: string | null }): boolean =>
  Boolean(person.is_staff) && (person.role_name || "").trim().toLowerCase() !== "staff";

/** Colore stabile per persona (stessa persona = stesso colore). */
const AVATAR_COLORS = [
  "#1677ff",
  "#13a8a8",
  "#389e0d",
  "#d46b08",
  "#c41d7f",
  "#531dab",
  "#08979c",
  "#d4380d",
  "#1d39c4",
  "#7cb305",
];

export const avatarColorOf = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) % 997;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// ---------------------------------------------------------------------------
// Colori
// ---------------------------------------------------------------------------

const normalizeHex = (hex: string): string | null => {
  const clean = (hex || "").trim().replace("#", "");
  if (/^[0-9a-fA-F]{3}$/.test(clean))
    return clean
      .split("")
      .map(c => c + c)
      .join("");
  if (/^[0-9a-fA-F]{6}$/.test(clean)) return clean;
  return null;
};

export const hexToRgba = (hex: string | undefined, alpha: number, fallback = "#1890ff"): string => {
  const clean = normalizeHex(hex || "") || normalizeHex(fallback) || "1890ff";
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const hexToRgb = (hex: string): [number, number, number] => {
  const clean = normalizeHex(hex) || "000000";
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
};

/** Coppie sfondo / testo in linea con la palette di Ant Design. */
export const STRUCTURE_PALETTE: { name: string; background_color: string; text_color: string }[] = [
  { name: "Blu", background_color: "#E6F4FF", text_color: "#0958D9" },
  { name: "Verde", background_color: "#F0F9EB", text_color: "#389E0D" },
  { name: "Arancio", background_color: "#FFF4E6", text_color: "#D46B08" },
  { name: "Viola", background_color: "#F6F0FF", text_color: "#722ED1" },
  { name: "Ciano", background_color: "#E6FFFB", text_color: "#08979C" },
  { name: "Magenta", background_color: "#FFF0F6", text_color: "#C41D7F" },
  { name: "Oro", background_color: "#FFFBE6", text_color: "#AD8B00" },
  { name: "Indaco", background_color: "#EEF2FF", text_color: "#3730A3" },
  { name: "Rosso", background_color: "#FFF1F0", text_color: "#CF1322" },
  { name: "Grigio", background_color: "#F5F5F5", text_color: "#595959" },
];

/** Primo colore della palette non ancora usato (o il meno usato). */
export const nextStructureColors = (
  structures: TOrgStructure[],
): { background_color: string; text_color: string } => {
  const usage = STRUCTURE_PALETTE.map(
    swatch =>
      structures.filter(s => (s.background_color || "").toUpperCase() === swatch.background_color)
        .length,
  );
  const min = Math.min(...usage);
  const swatch = STRUCTURE_PALETTE[usage.indexOf(min)];
  return { background_color: swatch.background_color, text_color: swatch.text_color };
};
